import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useLinkInterception} from '@github-ui/use-link-interception'
import type {Icon} from '@primer/octicons-react'
import {Timeline} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type React from 'react'
import {useRef} from 'react'

import {Ago} from './Ago'
import {EventActor, type EventActorFragment} from './EventActor'
import styles from './row.module.css'
import timelineRowStyles from './TimelineRow.module.css'
import {useTimelineRowPassthrough} from './TimelineRowPassthroughContext'

type TimelineRowType = {
  actor: EventActorFragment | null | undefined
  highlighted: boolean
  createdAt: string
  deepLinkUrl: string
  onLinkClick?: (event: MouseEvent) => void
  showAgoTimestamp?: boolean
  showActorName?: boolean
  refAttribute?: React.RefObject<HTMLLIElement | null>
  children: React.ReactNode
  leadingIcon: Icon
  iconColoring?: {
    color?: string
    backgroundColor?: string
  }
  fillRow?: boolean
  /**
   * Extra class name applied to the event's `Timeline.Item` `<li>`. Merged with
   * the component's own classes. Lets a consumer apply row-level treatments
   * (for example border-box or highlight-background styling) directly on the
   * list item instead of on an outer wrapper `<div>`.
   */
  rowClassName?: string
  /**
   * `data-*` attributes spread onto the event's `Timeline.Item` `<li>`, for
   * example `data-timeline-event-id`, `data-highlighted-event`, `data-testid`.
   * Keys with an `undefined` value are omitted.
   */
  rowDataAttributes?: Record<`data-${string}`, string | number | boolean | undefined>
  /**
   * A full-width divider rendered above the row content, inside the event's
   * `Timeline.Item` `<li>`. Because `Timeline.Item` lays its children out in a
   * flex row (badge then body), the divider is wrapped in a full-width block and
   * the `<li>` wraps so the divider sits on its own line above the row.
   */
  rowDivider?: React.ReactNode
}

type EventRowSlotsConfig = {
  trailing: typeof Trailing
  secondary: typeof Secondary
  main: typeof Main
}

type RowSlots = ReturnType<typeof useSlots<EventRowSlotsConfig>>

const Row = ({actor, children, ...props}: TimelineRowType) => {
  const slots = useSlots<EventRowSlotsConfig>(children, {
    trailing: Trailing,
    secondary: Secondary,
    main: Main,
  })

  return <RowBase actorData={actor ?? null} slots={slots} {...props} />
}

type RowBaseType = Omit<TimelineRowType, 'children' | 'actor'> & {
  slots: RowSlots
  actorData?: EventActorFragment | null
}

export const RowBase = ({
  showActorName = true,
  showAgoTimestamp = true,
  highlighted,
  createdAt,
  deepLinkUrl,
  onLinkClick,
  refAttribute,
  leadingIcon,
  iconColoring,
  slots,
  actorData,
  fillRow,
  rowClassName,
  rowDataAttributes,
  rowDivider,
}: RowBaseType) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  useLinkInterception({htmlContainerRef: containerRef, onLinkClick, openLinksInNewTab: false})

  // Consumers that cannot wrap the event <li> (issue-viewer under
  // primer_react_timeline_list_semantics, where an <ol> accepts only <li>
  // children) feed row-level treatments through context. Explicit props win over
  // context. With no provider, `passthrough` is undefined and the merges below
  // resolve to the original prop-only values, so the flag-off DOM is unchanged.
  const passthrough = useTimelineRowPassthrough()
  const effectiveRowClassName = clsx(passthrough?.rowClassName, rowClassName)
  const effectiveRowDataAttributes = {...passthrough?.rowDataAttributes, ...rowDataAttributes}
  const effectiveRowDivider = rowDivider ?? passthrough?.rowDivider

  return (
    <Timeline.Item
      ref={highlighted ? refAttribute : null}
      className={clsx(
        timelineRowStyles.Timeline_Item,
        isFeatureEnabled('issue_inline_avatars') && timelineRowStyles.Timeline_Item_inlineAvatars,
        passthrough?.rowIndent && timelineRowStyles.Timeline_Item_indent,
        effectiveRowDivider && timelineRowStyles.Timeline_Item_withDivider,
        effectiveRowClassName,
      )}
      style={{'--timelineBadge-bgColor': iconColoring?.backgroundColor}}
      {...effectiveRowDataAttributes}
    >
      {effectiveRowDivider ? <div className={timelineRowStyles.rowDivider}>{effectiveRowDivider}</div> : null}
      <Timeline.Badge className={clsx(highlighted && timelineRowStyles.highlightedBadge)}>
        {leadingIcon && (
          <span style={{color: iconColoring?.color}} className="d-flex">
            <Octicon icon={leadingIcon} className={timelineRowStyles.Octicon} />
          </span>
        )}
      </Timeline.Badge>
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <Timeline.Body ref={containerRef} className={clsx('TimelineBody', styles.body, fillRow && styles.fillRow)}>
        <div className={styles.timelineBodyRowContainer}>
          <div className={styles.timelineBodyContent}>
            <EventActor actor={actorData || null} showAvatarOnly={!showActorName} />
            {slots[0].main}
            {showAgoTimestamp && <Ago timestamp={new Date(createdAt)} linkUrl={deepLinkUrl} />}
          </div>
          <div className={styles.timelineBodyTrailingContent}>{slots[0].trailing}</div>
        </div>
        <div>{slots[0].secondary}</div>
      </Timeline.Body>
    </Timeline.Item>
  )
}

function Trailing(props: React.PropsWithChildren) {
  return <div data-trailing>{props.children}</div>
}

function Secondary(props: React.PropsWithChildren) {
  return <div data-secondary>{props.children}</div>
}

function Main(props: React.PropsWithChildren) {
  return <>{props.children}</>
}

export const TimelineRow = Object.assign(Row, {
  Trailing,
  Secondary,
  Main,
})
